<template>
  <div>
    <PageHeader />
    <b-card class="mb-3">
      <PaymentGatewayBalanceLogFilter :actors="balanceLogActors" :gateways="paymentGatewaySummaries" @submit="onSubmitFilter" />
    </b-card>

    <b-card>
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h6 class="mb-0">
          {{ `${$t('fields.result')} (${totalRows})` }}
        </h6>
        <LimitSelect v-model="limit" />
      </div>

      <b-table
          :busy="isFetchingBalanceLogs"
          :fields="fields"
          :items="balanceLogs.items"
          responsive
          show-empty
      >
        <template #cell(#)="data">
          {{ (limit * (page - 1)) + (data.index + 1) }}
        </template>
        <template #cell(createdAt)="data">
          {{ data.value | datetimeSecond }}
        </template>
        <template #cell(paymentGateway)="data">
          {{ gatewayName(data.item.paymentGateway) }}
        </template>
        <template #cell(agent)="data">
          {{ agentName(data.item) }}
        </template>
        <template #cell(type)="data">
          <b-badge :variant="typeInfo(data.value).variant">
            {{ typeInfo(data.value).label }}
          </b-badge>
        </template>
        <template #cell(amount)="data">
          <!-- ทิศทางดูจากเครื่องหมายของ amount ไม่ใช่จาก type เพราะ set เป็นได้ทั้งบวกและลบ -->
          <span :class="data.value < 0 ? 'text-danger' : 'text-success'">
            {{ data.value > 0 ? '+' : '' }}{{ data.value | currency }}
          </span>
        </template>
        <template #cell(beforeBalance)="data">
          {{ data.value | currency }}
        </template>
        <template #cell(afterBalance)="data">
          {{ data.value | currency }}
        </template>
        <template #cell(actor)="data">
          {{ actorName(data.item) }}
        </template>
        <template #cell(remark)="data">
          {{ data.value || '-' }}
        </template>
        <template #cell(refId)="data">
          <span class="text-muted">{{ data.value || '-' }}</span>
        </template>
        <template #table-busy>
          <div class="text-center my-2">
            <b-spinner class="align-middle" />
          </div>
        </template>
        <template #empty="">
          <p class="text-center text-muted">
            {{ $t('messages.nothing_here') }}
          </p>
        </template>
      </b-table>

      <PaginationInput :key="paginationKey" :per-page="limit" :total="totalRows" @update="(val) => (page = val)" />
    </b-card>
  </div>
</template>

<script>
import {mapActions, mapGetters, mapState} from 'vuex';
import {PAYMENT_GATEWAY, PAYMENT_GATEWAY_BALANCE_LOG_TYPE} from '@src/constants/payment-gateway';

export default {
  name: 'PaymentGatewayBalanceLog',
  page: {
    title: 'ประวัติการทำรายการ Payment Gateway',
  },
  components: {
    PaymentGatewayBalanceLogFilter: () => import('@components/payment-gateway/balance-log-filter'),
  },
  data() {
    return {
      page: 1,
      limit: 20,
      filters: {},
      // PaginationInput ไม่ sync prop หลัง mount เปลี่ยน key เพื่อให้กลับไปหน้า 1 ตอนเปลี่ยนเงื่อนไข
      paginationKey: 0,
    }
  },
  computed: {
    ...mapGetters(['isMaster']),
    ...mapState({
      paymentGatewaySummaries: (state) => state['payment-gateway'].paymentGatewaySummaries,
      balanceLogs: (state) => state['payment-gateway'].balanceLogs,
      balanceLogActors: (state) => state['payment-gateway'].balanceLogActors,
      isFetchingBalanceLogs: (state) => state['payment-gateway'].isFetchingBalanceLogs,
    }),
    totalRows() {
      return this.balanceLogs?.meta?.totalItems || 0
    },
    fields() {
      return [
        '#',
        {
          key: 'createdAt',
          label: `${this.$t('fields.createdAt')}`,
          thStyle: { minWidth: '150px' },
        },
        ...(this.isMaster
            ? [{
              key: 'agent',
              label: `${this.$t('agent.name')}`,
              thStyle: { minWidth: '120px' },
            }]
            : []),
        {
          key: 'paymentGateway',
          label: 'ผู้ให้บริการ',
          thStyle: { minWidth: '120px' },
        },
        {
          key: 'type',
          label: `${this.$t('fields.type')}`,
          class: 'text-center',
          thStyle: { minWidth: '170px' },
        },
        {
          key: 'amount',
          label: `${this.$t('fields.amount')}`,
          class: 'text-right',
          thStyle: { minWidth: '110px' },
        },
        {
          key: 'beforeBalance',
          label: 'ยอดก่อน',
          class: 'text-right',
          thStyle: { minWidth: '110px' },
        },
        {
          key: 'afterBalance',
          label: 'ยอดหลัง',
          class: 'text-right',
          thStyle: { minWidth: '110px' },
        },
        {
          key: 'actor',
          label: 'ผู้ทำรายการ',
          thStyle: { minWidth: '130px' },
        },
        {
          key: 'remark',
          label: `${this.$t('fields.remark')}`,
          thStyle: { minWidth: '180px' },
        },
        {
          // id ของรายการฝาก/ถอนต้นทาง มีเฉพาะรายการที่ระบบทำ
          key: 'refId',
          label: `${this.$t('fields.reference')}`,
          thStyle: { minWidth: '180px' },
        },
      ]
    }
  },
  watch: {
    page() {
      this.fetchBalanceLogs()
    },
    limit() {
      this.resetPageAndFetch()
    },
  },
  created() {
    // รายชื่อ payment gateway ใช้เป็นตัวเลือกใน filter (summary ตอบทุกตัวที่มองเห็นไม่ว่าช่วงวันไหน)
    this.getPaymentGatewaySummaries({ from: new Date(), to: new Date() })
    this.getPaymentGatewayBalanceLogActors()
  },
  methods: {
    ...mapActions([
      'getPaymentGatewaySummaries',
      'getPaymentGatewayBalanceLogs',
      'getPaymentGatewayBalanceLogActors',
    ]),
    agentName(item) {
      return item.agent?.name || '-'
    },
    // actor ต่อสดจากตาราง ส่วน actorName คือ username ตอนทำรายการ ถ้าไม่มีทั้งคู่แปลว่าระบบทำเอง
    actorName(item) {
      return item.actor?.name || item.actorName || 'ระบบ'
    },
    gatewayName(paymentGateway) {
      const name = paymentGateway?.name
      if (!name) {
        return '-'
      }
      return PAYMENT_GATEWAY[name]?.name || name
    },
    typeInfo(type) {
      return PAYMENT_GATEWAY_BALANCE_LOG_TYPE[type] || { label: type, variant: 'light' }
    },
    onSubmitFilter(filters) {
      this.filters = filters
      this.resetPageAndFetch()
    },
    resetPageAndFetch() {
      this.paginationKey += 1
      if (this.page !== 1) {
        // watcher ของ page จะ fetch ให้เอง
        this.page = 1
        return
      }
      this.fetchBalanceLogs()
    },
    fetchBalanceLogs() {
      const { paymentGatewayId, actorId, type, from, to } = this.filters
      this.getPaymentGatewayBalanceLogs({
        page: this.page,
        limit: this.limit,
        from: from || undefined,
        to: to || undefined,
        paymentGatewayId: paymentGatewayId || undefined,
        actorId: actorId || undefined,
        type: type && type.length > 0 ? type : undefined,
      })
    },
  },
}
</script>
